import { useNavigate } from 'react-router-dom';
import { UserIcon, UserGroupIcon, HeartIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { useSupportContext } from '@/contexts/SupportContext';

const audiences = [
  {
    id: 'older-adults',
    title: 'Older Adults',
    description: 'Resources for taking charge of your independence',
    icon: UserIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200',
    count: '45+ Resources'
  },
  {
    id: 'families',
    title: 'Families',
    description: 'Support guides for caring family members',
    icon: UserGroupIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50 border-green-200',
    count: '35+ Resources'
  },
  {
    id: 'caregivers',
    title: 'Caregivers',
    description: 'Professional tools and best practices',
    icon: HeartIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 border-purple-200',
    count: '40+ Resources'
  },
  {
    id: 'sponsors',
    title: 'Sponsors',
    description: 'Partnership and impact resources',
    icon: BuildingOfficeIcon,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 border-orange-200',
    count: '20+ Resources'
  }
];

const AudienceSelector = () => {
  const { selectedAudience, setSelectedAudience } = useSupportContext();
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Choose Your <span className="text-primary">Path to Success</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We've organized our resources specifically for your role and needs. 
            Select your audience to see tailored content that speaks directly to your situation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {audiences.map((audience) => {
            const IconComponent = audience.icon;
            const isSelected = selectedAudience === audience.id;
            
            return (
              <div
                key={audience.id}
                className={`cursor-pointer transition-all duration-300 rounded-lg border-2 p-6 ${
                  isSelected
                    ? `${audience.bgColor} scale-105 shadow-lg`
                    : 'bg-card border-border hover:border-primary/50 hover:shadow-md'
                }`}
                onClick={() => {
                  if (audience.id === 'older-adults') {
                    navigate('/support/older-adults');
                  } else {
                    setSelectedAudience(isSelected ? null : audience.id);
                  }
                }}
              >
                <div className="text-center">
                  <IconComponent className={`h-12 w-12 mx-auto mb-4 ${audience.color}`} />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {audience.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {audience.description}
                  </p>
                  <span className={`text-xs font-medium ${audience.color}`}>
                    {audience.count}
                  </span>
                </div>
                
                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-current/20">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        // This would filter content in a real implementation
                      }}
                    >
                      View Resources
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selectedAudience && (
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Resources below are now filtered for{' '}
              <span className="font-medium text-primary">
                {audiences.find(a => a.id === selectedAudience)?.title}
              </span>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AudienceSelector;